import React, { useCallback, useEffect, useRef, useState } from "react";
import "./RemoteAnalysisButton.css";
const activeStates = ["Pending", "Approved", "Running"];
const labels = {
    Pending: "Waiting for permission on the device", Approved: "Approved — waiting for the device",
    Running: "Five-minute analysis in progress", Completed: "PDF generated",
    Denied: "The device user declined", Cancelled: "Analysis cancelled", Expired: "Request expired",
    Failed: "Analysis could not finish", NoCredits: "No report credits available in your account",
    Queued: "Waiting to send", Sending: "Submitting to email provider", Accepted: "Accepted by email provider",
    Rejected: "Email provider rejected this copy", Uncertain: "Email acceptance could not be confirmed",
};
export default function RemoteAnalysisButton({ apiBaseUrl, token, targetUserId, targetName, meetingSessionId }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [devices, setDevices] = useState([]);
    const [deviceId, setDeviceId] = useState("");
    const [request, setRequest] = useState(null);
    const dialog = useRef(null);
    const requestId = useRef("");
    const context = `targetUserId=${targetUserId}${meetingSessionId ? `&meetingSessionId=${meetingSessionId}` : ""}`;
    const api = useCallback(async (path, method = "GET", body) => {
        if (!token)
            throw new Error("Sign in to request an analysis.");
        const response = await fetch(`${apiBaseUrl.replace(/\/$/, "")}/api/remote-analysis${path}`, {
            method, headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok)
            throw new Error(response.status === 401 ? "Sign in again to request an analysis."
                : data.message || "Remote analysis is unavailable. Try again shortly.");
        return data;
    }, [apiBaseUrl, token]);
    useEffect(() => {
        if (!open)
            return;
        if (!dialog.current?.open)
            dialog.current?.showModal();
        let cancelled = false;
        setBusy(true);
        setError("");
        setRequest(null);
        setDevices([]);
        requestId.current = crypto.randomUUID().replace(/-/g, "");
        Promise.all([api(`/devices?${context}`), api(`/latest?${context}`)])
            .then(([available, latest]) => {
            if (cancelled)
                return;
            setDevices(available.devices);
            setDeviceId(available.devices[0]?.deviceId || "");
            setRequest(latest.request);
        })
            .catch(e => { if (!cancelled)
            setError(e.message); })
            .finally(() => { if (!cancelled)
            setBusy(false); });
        return () => { cancelled = true; };
    }, [open, api, context]);
    const needsPolling = request && (activeStates.includes(request.state) || request.deliveries?.some(d => ["Queued", "Sending"].includes(d.state)));
    const polledRequestId = request?.requestId;
    useEffect(() => {
        if (!open || !needsPolling || !polledRequestId)
            return;
        let cancelled = false;
        let fetching = false;
        const timer = setInterval(async () => {
            if (fetching)
                return;
            fetching = true;
            try {
                const updated = await api(`/${polledRequestId}`);
                if (!cancelled) {
                    setRequest(updated);
                    setError("");
                }
            }
            catch {
                if (!cancelled)
                    setError("Status could not be refreshed. Your existing request is unchanged.");
            }
            finally {
                fetching = false;
            }
        }, 10000);
        return () => { cancelled = true; clearInterval(timer); };
    }, [open, api, needsPolling, polledRequestId]);
    async function submit(path, body) {
        if (busy)
            return;
        setBusy(true);
        setError("");
        try {
            setRequest(await api(path, "POST", body));
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "The request could not be confirmed. Retry the same request.");
        }
        finally {
            setBusy(false);
        }
    }
    const isActive = request && activeStates.includes(request.state);
    return <span className="remoteAnalysisAction" onClick={e => e.stopPropagation()}>
    <button type="button" className="secondaryButton compactAction remoteAnalyzeButton" disabled={targetUserId <= 0} onClick={() => setOpen(true)} title={targetUserId <= 0 ? "This participant must sign in to SignalTuner first" : "Request a PDF analysis with device permission"}>Analyze</button>
    {open && <dialog ref={dialog} className="remoteAnalysisDialog" onCancel={() => setOpen(false)} onClose={() => setOpen(false)} aria-label={`Analyze ${targetName}`}>
      <header><h2>Analyze {targetName}</h2><button type="button" aria-label="Close analysis request" onClick={() => setOpen(false)}>×</button></header>
      <p>A five-minute analysis creates a PDF emailed to you and this user. <strong>Your account pays one report credit.</strong></p>
      <p>The device user can choose Deny, Just once, or Always. Saved permission applies only to you on that device and can be revoked in their desktop Settings.</p>
      {error && <p role="alert" className="remoteAnalysisError">{error}</p>}
      {busy && <p role="status">Updating…</p>}
      {request ? <section aria-live="polite">
        <h3>{labels[request.state] || request.state}</h3>
        <p>{request.creditState === "Reserved" ? "One credit is reserved until the report completes. It is returned if analysis fails or is cancelled."
                    : request.creditState === "Consumed" ? "One report credit used. Email retries use no extra report credit."
                        : request.creditState === "Released" ? "The reserved credit was returned." : "No report credit used."}</p>
        {request.deliveries?.map(d => <p key={d.recipient}><strong>{d.recipient}:</strong> {labels[d.state] || d.state}</p>)}
        {request.deliveries?.some(d => d.state === "Uncertain") && <p>A copy may already have been sent. It will not be sent again automatically.</p>}
        <footer>
          {isActive && <button type="button" disabled={busy} onClick={() => void submit(`/${request.requestId}/cancel`)}>Cancel analysis</button>}
          {request.deliveries?.some(d => d.state === "Rejected") && <button type="button" disabled={busy} onClick={() => void submit(`/${request.requestId}/retry-delivery`)}>Retry rejected email</button>}
          {!isActive && <button type="button" disabled={busy} onClick={() => { requestId.current = crypto.randomUUID().replace(/-/g, ""); setRequest(null); setError(""); }}>New analysis</button>}
        </footer>
      </section> : !busy && <section>
        {devices.length > 0 ? <>
          <label>Device <select value={deviceId} onChange={e => setDeviceId(e.target.value)}>
            {devices.map(device => <option key={device.deviceId} value={device.deviceId}>{device.name}</option>)}
          </select></label>
          <footer><button type="button" disabled={busy || !deviceId} onClick={() => void submit("", {
                        requestId: requestId.current, targetUserId, deviceId, meetingSessionId: meetingSessionId || null,
                    })}>Request analysis · 1 credit</button></footer>
        </> : <p>The updated SignalTuner desktop app must be running and signed in on this user’s device. Reopen this dialog after they connect.</p>}
      </section>}
    </dialog>}
  </span>;
}
